import { get } from 'lodash'
import moment from 'moment'
import { GoogleAnalytics } from './google_analytics'

class SeatMapItems {
  constructor(bookingInformation, selectedSeats) {
    this.bookingInformation = bookingInformation
    this.selectedSeats = selectedSeats
  }

  createSeatMapItems() {
    const seatsList = this.bookingInformation.seats_list
    if (!seatsList) return []

    const passengerTypeDic = {
      ADULT: 'ADT',
      CHILD: 'CHD',
      INFANT: 'INF'
    }

    if (seatsList.length && this.selectedSeats) {
      const items = seatsList.map(e => {
        const segment = this.selectedSeats[e.flight_segment_reference_key]
        const passenger = segment.find(p => p.passenger_id === e.passenger_reference_key)
        if (passenger.seat_id === e.booked_seat) {
          const { departure_information: departure, arrival_information: arrival } = this.bookingInformation.flights_segment_information[e.flight_segment_reference_key]
          const passengerInfo = this.bookingInformation.passengers_information[e.passenger_reference_key]

          return {
            item_category: e.service_group_reference_id,
            item_id: `EXTRAS_${e.service_reference_id.split('_')[0]}`,
            item_name: `${departure.location_reference_id.split('_')[0]}/${arrival.location_reference_id.split('_')[0]}_${departure.date.split('-').reverse().join('-')}`,
            item_variant: passengerTypeDic[passengerInfo.passenger_type],
            price: passenger.service_associated_key && passenger.service_associated_key.total.amount || 0,
            quantity_extra: 1
          }
        }
      })

      return items
    }
  }
}

class AddPassenger extends GoogleAnalytics {
  process () {
    const passengerData = this.getAddPassengerData()
    this.data = {
      ecommerce: {
        item_list_id: this.createItemListId(),
        ...this.searchFlightData,
        ...passengerData
      }
    }
    return this.pushEvent()
  }

  getAddPassengerData () {
    const item = {
      category: 'traveller_details',
      action: 'add_passenger',
      adt: {
        male: 0,
        female: 0
      },
      chd: {
        male: 0,
        female: 0
      },
      inf: {
        male: 0,
        female: 0
      },
      nationality: null
    }

    for (const passengerType in this.itemData) {
      const element = this.itemData[passengerType]
      if (element && element.length) {
        element.forEach((e) => {
          item[e.passenger_type.toLowerCase()][e.info.gender === 'M' ? 'male' : 'female']++
        })
      }
    }

    return item
  }
}

class ClickEvent extends GoogleAnalytics {
  process () {
    this.data = { 
      ecommerce: {
        item_list_id: this.createItemListId(),
        ...this.searchFlightData
      }
    }
    return this.pushEvent()
  }
}

class ExtraServiceGroupView extends GoogleAnalytics {
  constructor (tab, passenger, serviceGroupReferenceId, itemListId, ...args) {
    super(...args)
    this.tab = tab
    this.passengerInfo = passenger
    this.serviceGroupReferenceId = serviceGroupReferenceId
    this.itemListId = itemListId
  }

  process () {
    this.data = this.getExtraServiceGroupViewData()

    return this.pushEvent()
  }

  getExtraServiceGroupViewData () {
    return {
      ecommerce: {
        item_list_id: this.itemListId,
        item_list_name: 'EXTRA_SERVICES',
        items: this.createItemsByTab()
      }
    }
  }

  createItemsByTab () {
    const items = []

    const passengerTypeDic = {
      ADULT: 'ADT',
      CHILD: 'CHD',
      INFANT: 'INF'
    }

    const departureDate = !this.tab.applied_at_journey ? (this.tab.trip_initial_departure_date || this.tab.departure_date).split('-').reverse().join('-') : null
    const departureLocation = this.tab.departure_location_reference_id.split('_')[0]
    const arrivalLocation = this.tab.arrival_location_reference_id.split('_')[0]
    const itemName = this.tab.applied_at_journey ? `${departureLocation}/${arrivalLocation}` : `${departureLocation}/${arrivalLocation}_${departureDate}`

    for (const service of this.tab.services_list) {
      for (const passenger of service.passengers_list) {
        items.push({
          item_id: `EXTRAS_${service.booking_instructions.service_reference_id.split('_')[0]}`,
          item_name: itemName,
          item_category: this.serviceGroupReferenceId,
          item_variant: passengerTypeDic[this.passengerInfo[passenger.passenger_reference_key].passenger_type],
          price: (passenger.passenger_service_price && passenger.passenger_service_price.total && passenger.passenger_service_price.total.amount) || 0
        })
      }
    }

    return items
  }
}

class ExtraService extends GoogleAnalytics {
  getExtraServiceData () {
    const items = this.createItems()
    const value = items.reduce((acc, item) => (acc = acc + item.price), 0)

    return {
      ecommerce: {
        value,
        items
      }
    }
  }

  process () {
    this.data = this.getExtraServiceData()

    return this.pushEvent()
  }

  createItems () {
    const items = []
    const passengerTypeDic = {
      ADULT: 'ADT',
      CHILD: 'CHD',
      INFANT: 'INF'
    }

    for (const item of this.itemData) {
      const { tab, service, passenger, passengersInfo, serviceGroupReferenceId } = item
      const departureDate = !tab.applied_at_journey ? (tab.trip_initial_departure_date || tab.departure_date).split('-').reverse().join('-') : null
      const departureLocation = tab.departure_location_reference_id.split('_')[0]
      const arrivalLocation = tab.arrival_location_reference_id.split('_')[0]
      const itemName = tab.applied_at_journey ? `${departureLocation}/${arrivalLocation}` : `${departureLocation}/${arrivalLocation}_${departureDate}`

      items.push({
        item_id: `EXTRAS_${service.booking_instructions.service_reference_id.split('_')[0]}`,
        item_name: itemName,
        item_category: serviceGroupReferenceId,
        item_variant: passengerTypeDic[passengersInfo[passenger.passenger_reference_key].passenger_type],
        price: (passenger.passenger_service_price && passenger.passenger_service_price.total && passenger.passenger_service_price.total.amount) || 0,
        quantity_extra: (passenger.booked_service_information && (this.eventName === 'extra_service_remove_from_cart' ? passenger.booked_service_information.services_quantity : passenger.booked_service_information.service_booked_quantity)) || 1
      })
    }

    return items
  }
}

class EndOfFlow extends GoogleAnalytics {
  constructor (extraServicesItems, value, coupon, flights, bookingCode, tax, fee, status, transactionId, ...args) {
    super(...args)
    this.extraServicesItems = extraServicesItems
    this.value = value
    this.coupon = coupon
    this.flights = flights
    this.bookingCode = bookingCode
    this.tax = tax
    this.fee = fee
    this.status = status
    this.transactionId = transactionId
  }

  getEndOfFlowData () {
    return {
      ecommerce: {
        item_list_id: this.createItemListId(),
        currency: this.currency,
        transaction_id: this.transactionId,
        value: this.formatValue(this.value),
        coupon: this.coupon,
        tax: this.formatValue(this.tax + this.fee),
        items: [
          ...this.getItemsByVariant(),
          ...this.extraServicesItems.items,
          ...this.extraServicesItems.seatMapItems
        ],
        ...this.searchFlightData
      }
    }
  }

  process () {
    this.data = this.getEndOfFlowData()
    return this.pushEvent()
  }
}

class AddPaymentInfo extends GoogleAnalytics {
  constructor (value, coupon, paymentType, flights, bookingCode, ...args) {
    super(...args)
    this.value = value
    this.coupon = coupon
    this.paymentType = paymentType
    this.flights = flights
    this.bookingCode = bookingCode
  }

  getAddPaymentInfoData () {
    return {
      ecommerce: {
        item_list_id: this.createItemListId(),
        currency: this.currency,
        value: this.formatValue(this.value),
        coupon: this.coupon,
        payment_type: this.paymentType,
        items: this.getItemsByVariant(),
        ...this.searchFlightData
      }
    }
  }

  process () {
    this.data = this.getAddPaymentInfoData()
    return this.pushEvent()
  }
}

class AddToCart extends GoogleAnalytics {
  constructor (value, flights, ...args) {
    super(...args)
    this.value = value
    this.flights = flights
  }

  process () {
    this.data = this.getAddFlightToCartData()
    return this.pushEvent()
  }

  getAddFlightToCartData () {
    return {
      ecommerce: {
        item_list_id: this.createItemListId(),
        currency: this.currency,
        value: this.formatValue(this.value),
        items: this.getItemsByVariant(),
        ...this.searchFlightData
      }
    }
  }
}

class ItemListView extends GoogleAnalytics {
  constructor (amountPerPax = false, ...args) {
    super(...args)
    this.amountPerPax = amountPerPax
  }

  getItemListViewData () {
    return {
      ecommerce: {
        item_list_id: this.createItemListId(),
        item_list_name: 'FLIGHT',
        currency: this.currency,
        items: this.getItems(),
        ...this.searchFlightData
      }
    }
  }

  process () {
    this.data = this.getItemListViewData()
    return this.pushEvent()
  }

  componentSegment (segmentSplit) {
    let segment = segmentSplit.substring(0, 3).concat('/').concat(segmentSplit.substring(3))
    return segment
  }

  getItems () {
    try {
      let items = new ItemView(false, false, this.amountPerPax, null, this.itemData).getItemList()
      return items
    } catch (error) {
      console.error('error create items list. ' + error)
      return []
    }
  }
}

class ItemView extends GoogleAnalytics {
  constructor (isReturnFlight, isRoundTrip, amountPerPax = false, ...args) {
    super(...args)
    this.isReturnFlight = isReturnFlight
    this.isRoundTrip = isRoundTrip
    this.amountPerPax = amountPerPax
  }

  process () {
    this.data = {
      ecommerce: this._getItems()
    }
    return this.pushEvent()
  }

  _getItems () {
    const flight = this.itemData.flight
    const flightSplit = this.flightSplit(flight)
    const itemId = this.createItemId(flightSplit)
    const itemName = this.createItemName(flightSplit)
    const fare = this.itemData.flight.fares[this.itemData.fare.name]
    const typeOfFlight = this.getTypeOfFlight(flight)
    const flightDuration = this.getFlightDuration(flight)
    const ffam = this.itemData.fare.name.split('_')[0]
    const rbd = this.getRbd(fare.rbd)
    const departureTime = this.formatTime(this.getDepartureTime(this.itemData))
    const value = ((this.isRoundTrip && this.isReturnFlight) || !this.isRoundTrip) ? this.getValue(fare) : null
    const adults = this.itemData.adults
    const childs = this.itemData.minors
    const infants = this.itemData.infants
    const itemObject = this.getItemView(
      false,
      value,
      ffam,
      rbd,
      typeOfFlight,
      flightDuration,
      departureTime,
      this.resolvePrice(fare),
      adults,
      childs,
      infants,
      itemId,
      itemName
    )
    return itemObject
  }

  resolvePrice (fare) {
    return this.amountPerPax ? fare.average_amount_per_pax_adt : fare.average_amount
  }

  flightSplit (journey) {
    return journey.flights[0].split('_')
  }

  getDepartureTime (data) {
    try {
      return data.source[this.itemData.flight.flights[0]].departure_information.time
    } catch (error) {
      console.error('error get departure time')
      return '00:00:00'
    }
  }

  getItemList () {
    const journeys = this.itemData.journeys
    let itemList = []
    for (const jr in journeys) {
      const flightSplit = this.flightSplit(journeys[jr])
      const itemId = this.createItemId(flightSplit)
      const itemName = this.createItemName(flightSplit)
      const fares = journeys[jr].fares
      for (const fr in fares) {
        if (fares[fr].average_amount !== null && fares[fr].rbd.posting > 0) {
          const ffam = fr.split('_')[0]
          const typeOfFlight = this.getTypeOfFlight(journeys[jr])
          const flightDuration = this.formatTime(this.getFlightDuration(journeys[jr]))
          const flightTime = this.formatTime(this.itemData.flights[journeys[jr].flights[0]].departure_information.time)
          const adults = this.itemData.adults
          const childs = this.itemData.minors
          const infants = this.itemData.infants
          let itemObject = this.getItemView(true, 0, ffam,
            this.getRbd(fares[fr].rbd), typeOfFlight, flightDuration,
            flightTime, this.resolvePrice(fares[fr]), adults,
            childs, infants, itemId, itemName)
          itemList.push(itemObject)
        }
      }
    }

    return itemList
  }

  createItemId (flightSplit) {
    const flight = flightSplit[5].split('-')[1].replace('FL00', '')
    const segment = flightSplit[1].substring(0, 3).concat('/').concat(flightSplit[1].substring(3))
    const date = moment(flightSplit[3].replace('-CPA', '')).format('DD-MM-YYYY')
    return flight.concat('_' + segment + '_' + date)
  }

  createItemName (flightSplit) {
    return flightSplit[1].substring(0, 3).concat('/').concat(flightSplit[1].substring(3))
  }

  getFlightDuration (flight) {
    // item_category 4
    return get(flight.order, 'total_time')
  }

  getTypeOfFlight (flight) {
    // item_category3
    let type = ''
    const connections = get(flight.order, 'connection_qty') > 0 ? get(flight.order, 'connection_qty') + ' CONNECTIONS' : ''
    const stops = get(flight.order, 'total_stops') > 0 ? get(flight.order, 'total_stops') + ' STOPS' : ''
    if (stops === '' && connections === '') {
      return 'DIRECT'
    }
    type = connections.concat('' + stops)
    return type
  }
}

export {
  ItemListView,
  ItemView,
  AddToCart,
  AddPaymentInfo,
  EndOfFlow,
  ExtraService,
  ExtraServiceGroupView,
  ClickEvent,
  AddPassenger,
  SeatMapItems
}
